/**
 * Events dashboard
 *
 * - Stat tiles: events in the next 30 days, events held for review, Planning Center last sync and
 *   recent sync problems
 * - The next 30 days, plus a Planning Center panel with the last run and recent errors
 *
 * Data: GET api/events/admin (next 30 days), api/events/review, api/events/sources and the runs of
 * the Planning Center connection. Each panel handles its own failure.
 */

import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconChevronRight,
  IconClipboardCheck,
  IconPlugConnected,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Alert, Box, Button, Grid, Group, SimpleGrid, Skeleton, Stack, Text } from '@/components/ui';
import { EmptyState } from '@/components/ui/data-display';
import { FadeIn } from '@/components/animations';
import {
  useEventAdminService,
  useEventSourceService,
  EventSourceIds,
  EventSyncRunStatusIds,
  type EventAdminListItem,
  type EventSourceConnectionView,
} from '@actschurch/ccms-sdk';
import { useEventsPermissions } from '@/hooks/events/useEventsPermissions';
import {
  ErrorNotice,
  EVENTS_LIST_PATH,
  NEW_EVENT_PATH,
  PageHeader,
  REVIEW_PATH,
  ReviewBadge,
  RunStatusBadge,
  SOURCES_PATH,
  SectionCard,
  SourceBadge,
  StatTile,
  StatusBadge,
  Tag,
  addDays,
  campusLabel,
  dateToYmd,
  eventPath,
  eventsKeys,
  formatDateTime,
  formatWhen,
  isHeld,
  plural,
  startOfToday,
  timeAgo,
} from '../components';

const UPCOMING_DAYS = 30;

function pickPlanningCenter(list: EventSourceConnectionView[] | undefined): EventSourceConnectionView | undefined {
  if (!list?.length) return undefined;
  return list.find((c) => c.sourceID === EventSourceIds.PlanningCenterCalendar) ?? list[0];
}

export default function EventsDashboard() {
  const navigate = useNavigate();
  const perms = useEventsPermissions();
  const eventAdmin = useEventAdminService();
  const eventSource = useEventSourceService();

  const today = startOfToday();
  const upcomingQuery = { from: dateToYmd(today), to: dateToYmd(addDays(today, UPCOMING_DAYS)), skip: 0, take: 8 };
  const upcoming = useQuery({
    queryKey: eventsKeys.adminList(upcomingQuery),
    queryFn: () => eventAdmin.listAdmin(upcomingQuery),
    staleTime: 60_000,
    retry: 1,
  });
  const review = useQuery({
    queryKey: eventsKeys.review,
    queryFn: () => eventAdmin.getReviewQueue(),
    enabled: perms.canReview,
    staleTime: 60_000,
    retry: 1,
  });
  const sources = useQuery({
    queryKey: eventsKeys.sources,
    queryFn: () => eventSource.list(),
    enabled: perms.canViewSources,
    staleTime: 60_000,
    retry: 1,
  });
  const pc = pickPlanningCenter(sources.data);
  const runs = useQuery({
    queryKey: eventsKeys.runs(pc?.connectionID ?? 0),
    queryFn: () => eventSource.getRuns(pc!.connectionID, 10),
    enabled: !!pc,
    staleTime: 60_000,
    retry: 1,
  });

  const problemRuns = (runs.data ?? []).filter(
    (r) => r.statusID === EventSyncRunStatusIds.Failed || r.statusID === EventSyncRunStatusIds.Partial || !!r.error
  );
  const heldCount = review.data?.length ?? 0;
  const upcomingItems = upcoming.data?.items ?? [];

  const refreshAll = () => {
    upcoming.refetch();
    if (perms.canReview) review.refetch();
    if (perms.canViewSources) sources.refetch();
    if (pc) runs.refetch();
  };
  const refreshing = upcoming.isFetching || review.isFetching || sources.isFetching || runs.isFetching;

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <PageHeader
            helpKey="events.dashboard"
            title="Events"
            description="The church calendar from CCMS and Planning Center, in one place."
            actions={
              <>
                <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={refreshAll} loading={refreshing && !upcoming.isLoading}>
                  Refresh
                </Button>
                {perms.canCreate && (
                  <Button leftSection={<IconPlus size={16} />} component={Link} to={NEW_EVENT_PATH} data-help-id="events.dashboard.new-event">
                    New event
                  </Button>
                )}
              </>
            }
          />

          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }}>
            <StatTile
              helpId="events.dashboard.stat-upcoming"
              icon={<IconCalendarEvent size={22} />}
              color="blue"
              label={`Next ${UPCOMING_DAYS} days`}
              value={upcoming.isLoading ? '...' : upcoming.error ? 'Not available' : plural(upcoming.data?.totalCount ?? 0, 'event')}
              hint="Open the events list"
              onClick={() => navigate(EVENTS_LIST_PATH)}
            />
            <StatTile
              helpId="events.dashboard.stat-review"
              icon={<IconClipboardCheck size={22} />}
              color="orange"
              label="Held for review"
              value={!perms.canReview ? 'No access' : review.isLoading ? '...' : review.error ? 'Not available' : plural(heldCount, 'event')}
              hint={heldCount > 0 ? 'Map them so they show on the calendar' : 'Nothing is waiting'}
              emphasis={heldCount > 0}
              onClick={perms.canReview ? () => navigate(REVIEW_PATH) : undefined}
            />
            <StatTile
              helpId="events.dashboard.stat-sync"
              icon={<IconPlugConnected size={22} />}
              color="grape"
              label="Planning Center sync"
              value={!perms.canViewSources ? 'No access' : sources.isLoading ? '...' : pc ? timeAgo(pc.lastRunAt) : 'Not set up'}
              hint={pc ? (pc.isEnabled ? pc.lastRunStatusName || 'Enabled' : 'Switched off') : undefined}
              onClick={perms.canViewSources ? () => navigate(SOURCES_PATH) : undefined}
            />
            <StatTile
              helpId="events.dashboard.stat-problems"
              icon={<IconAlertTriangle size={22} />}
              color={problemRuns.length > 0 ? 'red' : 'teal'}
              label="Sync problems"
              value={!perms.canViewSources ? 'No access' : runs.isLoading && !!pc ? '...' : plural(problemRuns.length, 'run')}
              hint="In the last 10 runs"
              emphasis={problemRuns.length > 0}
              onClick={perms.canViewSources ? () => navigate(SOURCES_PATH) : undefined}
            />
          </SimpleGrid>

          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 8 }}>
              <SectionCard
                helpId="events.dashboard.upcoming"
                icon={<IconCalendarEvent size={18} />}
                title={`Next ${UPCOMING_DAYS} days`}
                description="Every event, including ones held for review."
                actions={
                  <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} component={Link} to={EVENTS_LIST_PATH}>
                    All events
                  </Button>
                }
              >
                <UpcomingList query={upcoming} items={upcomingItems} onOpen={(id) => navigate(eventPath(id))} />
              </SectionCard>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="lg">
                <SectionCard
                  helpId="events.dashboard.planning-center"
                  icon={<IconPlugConnected size={18} />}
                  color="grape"
                  title="Planning Center"
                  actions={
                    perms.canViewSources ? (
                      <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} component={Link} to={SOURCES_PATH}>
                        Sources
                      </Button>
                    ) : undefined
                  }
                >
                  {!perms.canViewSources ? (
                    <Text size="sm" c="dimmed">
                      You do not have access to the source settings.
                    </Text>
                  ) : sources.isLoading ? (
                    <Skeleton height={90} radius="md" />
                  ) : sources.error ? (
                    <ErrorNotice error={sources.error} title="Could not load the sources" onRetry={() => sources.refetch()} />
                  ) : !pc ? (
                    <Text size="sm" c="dimmed">
                      No source is set up yet.
                    </Text>
                  ) : (
                    <Stack gap="sm">
                      {!pc.isConfigured && (
                        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                          Planning Center credentials are not set up on the server yet.
                        </Alert>
                      )}
                      <Group gap="xs" wrap="wrap">
                        <RunStatusBadge statusID={pc.lastRunStatusID} name={pc.lastRunStatusName} />
                        {!pc.isEnabled && <Tag>Switched off</Tag>}
                      </Group>
                      <Text size="sm">
                        Last sync: <strong>{formatDateTime(pc.lastRunAt, 'Never')}</strong>
                      </Text>
                      {pc.lastRunSummary && (
                        <Text size="sm" c="dimmed">
                          {pc.lastRunSummary}
                        </Text>
                      )}
                      {pc.lastError && (
                        <Text size="sm" c="red">
                          {pc.lastError}
                        </Text>
                      )}
                    </Stack>
                  )}
                </SectionCard>

                {perms.canViewSources && pc && (
                  <SectionCard helpId="events.dashboard.recent-errors" icon={<IconAlertTriangle size={18} />} color="red" title="Recent errors" description="Failed or partial runs.">
                    {runs.isLoading ? (
                      <Skeleton height={60} radius="md" />
                    ) : runs.error ? (
                      <ErrorNotice error={runs.error} title="Could not load the runs" onRetry={() => runs.refetch()} />
                    ) : problemRuns.length === 0 ? (
                      <Text size="sm" c="dimmed">
                        No errors in the last 10 runs.
                      </Text>
                    ) : (
                      <Stack gap="sm">
                        {problemRuns.slice(0, 5).map((r) => (
                          <Box key={r.runID}>
                            <Group gap="xs" justify="space-between" wrap="nowrap">
                              <Text size="sm" fw={600}>
                                {formatDateTime(r.startedAt)}
                              </Text>
                              <RunStatusBadge statusID={r.statusID} name={r.statusName} />
                            </Group>
                            <Text size="xs" c="dimmed" lineClamp={2}>
                              {r.error || 'Deletions were skipped on this run.'}
                            </Text>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}

function UpcomingList({
  query,
  items,
  onOpen,
}: {
  query: { isLoading: boolean; error: unknown; refetch: () => unknown };
  items: EventAdminListItem[];
  onOpen: (id: number) => void;
}) {
  if (query.isLoading) {
    return (
      <Stack gap="sm">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={52} radius="md" />
        ))}
      </Stack>
    );
  }
  if (query.error) {
    return <ErrorNotice error={query.error} title="Could not load the upcoming events" onRetry={() => query.refetch()} />;
  }
  if (items.length === 0) {
    return <EmptyState icon={<IconCalendarEvent size={36} />} title="No events in the next 30 days" description="New and synced events will show here." size="sm" />;
  }
  return (
    <Stack gap={0}>
      {items.map((e, idx) => (
        <Box
          key={e.eventID}
          onClick={() => onOpen(e.eventID)}
          style={{
            cursor: 'pointer',
            padding: '10px 4px',
            borderTop: idx === 0 ? undefined : '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={1}>
                {e.name || 'Untitled event'}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                {formatWhen(e)} · {campusLabel(e)}
                {e.typeName ? ` · ${e.typeName}` : ''}
              </Text>
            </Stack>
            <Group gap={4} wrap="nowrap">
              <SourceBadge sourceID={e.sourceID} name={e.sourceName} detached={e.isDetached} />
              {isHeld(e) && <ReviewBadge reviewStatusID={e.reviewStatusID} name={e.reviewStatusName} />}
              {e.statusID !== 1 && <StatusBadge statusID={e.statusID} name={e.statusName} />}
            </Group>
          </Group>
        </Box>
      ))}
    </Stack>
  );
}
